import React from 'react';
import { ArrowDown } from 'lucide-react';

interface HeroProps {
  onJoinWaitlist: () => void;
}

// Word delays for the staggered headline entrance (CSS animation, no GSAP)
const wordDelays = ['0.7s', '0.8s', '0.9s', '1.05s', '1.15s', '1.25s'];

const Hero: React.FC<HeroProps> = ({ onJoinWaitlist }) => {
  return (
    <section
      className="relative min-h-screen flex flex-col justify-center overflow-hidden"
      style={{
        background: 'linear-gradient(150deg, #1E2A24 0%, #293932 35%, #3A4D42 60%, #2E3D35 80%, #1E2A24 100%)',
      }}
      aria-label="Hero — Salford & Co."
    >
      {/* ── Decorative organic orbs (CSS-only floating) ── */}
      <div
        className="hero-orb-1 absolute top-[12%] right-[8%] w-[520px] h-[520px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 40% 40%, rgba(76,90,62,0.55) 0%, rgba(41,57,50,0) 70%)',
          filter: 'blur(1px)',
        }}
        aria-hidden="true"
      />
      <div
        className="hero-orb-2 absolute bottom-[8%] left-[4%] w-[420px] h-[420px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 60% 60%, rgba(164,133,115,0.18) 0%, rgba(41,57,50,0) 70%)',
          filter: 'blur(2px)',
        }}
        aria-hidden="true"
      />
      <div
        className="hero-orb-3 absolute top-[45%] left-[25%] w-[350px] h-[350px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(107,123,94,0.2) 0%, rgba(41,57,50,0) 65%)',
          filter: 'blur(1px)',
        }}
        aria-hidden="true"
      />

      {/* ── Botanical SVG accent ── */}
      <svg
        className="absolute top-0 right-0 w-[380px] h-[380px] opacity-[0.07] pointer-events-none"
        viewBox="0 0 400 400"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="380" cy="20" r="200" stroke="#FAF8F5" strokeWidth="0.5" />
        <circle cx="380" cy="20" r="150" stroke="#FAF8F5" strokeWidth="0.5" />
        <circle cx="380" cy="20" r="100" stroke="#FAF8F5" strokeWidth="0.5" />
        <line x1="0" y1="400" x2="400" y2="0" stroke="#FAF8F5" strokeWidth="0.5" />
        <line x1="0" y1="300" x2="400" y2="0" stroke="#FAF8F5" strokeWidth="0.3" />
        <line x1="0" y1="400" x2="300" y2="0" stroke="#FAF8F5" strokeWidth="0.3" />
      </svg>

      {/* ── Vertical grid lines ── */}
      <svg
        className="absolute inset-0 w-full h-full opacity-[0.04] pointer-events-none"
        aria-hidden="true"
      >
        <line x1="25%" y1="0" x2="25%" y2="100%" stroke="#FAF8F5" strokeWidth="0.5" />
        <line x1="50%" y1="0" x2="50%" y2="100%" stroke="#FAF8F5" strokeWidth="0.5" />
        <line x1="75%" y1="0" x2="75%" y2="100%" stroke="#FAF8F5" strokeWidth="0.5" />
      </svg>

      {/* ── Horizontal divider lines ── */}
      <div className="absolute top-1/4 left-0 right-0 h-[0.5px] bg-gradient-to-r from-transparent via-[#FAF8F5]/6 to-transparent pointer-events-none" aria-hidden="true" />
      <div className="absolute bottom-1/4 left-0 right-0 h-[0.5px] bg-gradient-to-r from-transparent via-[#FAF8F5]/6 to-transparent pointer-events-none" aria-hidden="true" />

      {/* ── CONTENT ── */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 sm:px-10 lg:px-16 w-full pt-32 pb-24 lg:pt-40 lg:pb-32">
        {/* Badge */}
        <div
          className="flex items-center gap-3 mb-10"
          aria-hidden="true"
          style={{ animation: 'fadeUp 0.8s cubic-bezier(0.22, 1, 0.36, 1) 0.5s both' }}
        >
          <div className="w-10 h-[1px] bg-[#A48573]/45" />
          <span className="font-mono text-[10px] uppercase tracking-[0.38em] text-[#FAF8F5]/45">
            Est. 2024
          </span>
          <span className="w-1 h-1 rounded-full bg-[#A48573]/50" />
          <span className="font-mono text-[10px] uppercase tracking-[0.38em] text-[#FAF8F5]/45">
            Organic Wellness
          </span>
          <div className="w-10 h-[1px] bg-[#A48573]/45" />
        </div>

        {/* Headline — words stagger in via CSS animation */}
        <h1
          className="font-display leading-[0.93] mb-8"
          aria-label="Rooted in Nature, Made for You"
        >
          {/* Line 1 */}
          <span className="block overflow-hidden">
            {(['Rooted', 'in', 'Nature'] as const).map((word, i) => (
              <React.Fragment key={word}>
                <span
                  className={`hword inline-block text-[clamp(3rem,7.5vw,7rem)] ${
                    i === 2 ? 'italic font-extralight text-[#C8BDAF]' : 'font-bold text-[#FAF8F5]'
                  }`}
                  style={{ animation: `fadeUp 1.1s cubic-bezier(0.16, 1, 0.3, 1) ${wordDelays[i]} both` }}
                >
                  {word}
                </span>
                {i < 2 && ' '}
              </React.Fragment>
            ))}
          </span>

          {/* Line 2 */}
          <span className="block overflow-hidden">
            {(['Made', 'for', 'You'] as const).map((word, i) => (
              <React.Fragment key={word}>
                <span
                  className={`hword inline-block text-[clamp(3rem,7.5vw,7rem)] ${
                    i === 2 ? 'italic font-extralight text-[#C8BDAF]' : 'font-bold text-[#FAF8F5]'
                  }`}
                  style={{ animation: `fadeUp 1.1s cubic-bezier(0.16, 1, 0.3, 1) ${wordDelays[i + 3]} both` }}
                >
                  {word}
                </span>
                {i < 2 && ' '}
              </React.Fragment>
            ))}
          </span>
        </h1>

        {/* Taupe accent divider */}
        <div className="w-16 h-[2px] bg-[#A48573]/50 mb-8" aria-hidden="true" />

        {/* Subtitle */}
        <p
          className="font-body text-lg sm:text-xl text-[#FAF8F5]/65 max-w-[500px] leading-relaxed mb-12"
          style={{ animation: 'fadeUp 0.8s cubic-bezier(0.22, 1, 0.36, 1) 1.35s both' }}
        >
          Premium organic skincare crafted with sustainably sourced botanical
          ingredients. Where ancient wisdom meets modern science for
          transformative results.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-4 items-start">
          <a
            href="#products"
            className="group inline-flex items-center gap-3 px-10 py-4 bg-[#A48573] text-[#FAF8F5] font-mono text-[11px] uppercase tracking-[0.28em] hover:bg-[#8B7260] transition-all duration-500 shadow-[0_8px_32px_rgba(164,133,115,0.3)]"
            style={{ animation: 'fadeUp 0.6s cubic-bezier(0.22, 1, 0.36, 1) 1.5s both' }}
          >
            Explore Collection
            <ArrowDown
              size={14}
              className="group-hover:translate-y-1 transition-transform duration-300"
            />
          </a>
          <button
            onClick={onJoinWaitlist}
            className="inline-flex items-center gap-3 px-10 py-4 border border-[#FAF8F5]/20 text-[#FAF8F5] font-mono text-[11px] uppercase tracking-[0.28em] hover:bg-[#FAF8F5]/8 hover:border-[#FAF8F5]/35 transition-all duration-500"
            style={{ animation: 'fadeUp 0.6s cubic-bezier(0.22, 1, 0.36, 1) 1.62s both' }}
          >
            Our Story
          </button>
        </div>

        {/* Trust badges */}
        <div
          className="flex flex-wrap gap-7 mt-16"
          style={{ animation: 'fadeIn 0.8s ease-out 1.8s both' }}
        >
          {['100% Organic', 'Cruelty Free', 'Sustainable', 'Vegan'].map((badge) => (
            <span
              key={badge}
              className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FAF8F5]/30 flex items-center gap-2"
            >
              <span className="w-1 h-1 rounded-full bg-[#A48573]/50" aria-hidden="true" />
              {badge}
            </span>
          ))}
        </div>
      </div>

      {/* Scroll indicator — CSS animation only */}
      <div
        className="scroll-indicator absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2"
        aria-hidden="true"
      >
        <span className="font-mono text-[9px] uppercase tracking-[0.38em] text-[#FAF8F5]/22">
          Scroll
        </span>
        <div
          className="scroll-line w-[1px] h-10 bg-gradient-to-b from-[#FAF8F5]/30 to-transparent"
        />
      </div>
    </section>
  );
};

export default Hero;
